import type { Project, ProjectResourceAssignment, SolutionItem, UserResource } from "@/engine/types";

/* ------------------------------------------------------------------------------------------------
 * Month helpers ("YYYY-MM")
 * ---------------------------------------------------------------------------------------------- */

export type YearMonth = string;

export function monthOf(iso: string): YearMonth {
  return iso.slice(0, 7);
}

export function monthsBetween(startISO: string, endISO: string): YearMonth[] {
  const out: YearMonth[] = [];
  if (!startISO || !endISO) return out;
  let y = Number(startISO.slice(0, 4));
  let m = Number(startISO.slice(5, 7));
  const ey = Number(endISO.slice(0, 4));
  const em = Number(endISO.slice(5, 7));
  let guard = 0;
  while ((y < ey || (y === ey && m <= em)) && guard++ < 240) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return out;
}

export function quarterOf(ym: YearMonth): string {
  const y = ym.slice(0, 4);
  const m = Number(ym.slice(5, 7));
  return `${y} Q${Math.floor((m - 1) / 3) + 1}`;
}

/** Quarter key ("2026 Q3") → first month of that quarter ("2026-07"), for formatDate(…, "quarter"). */
export function quarterStartMonth(q: string): YearMonth {
  const y = q.slice(0, 4);
  const n = Number(q.slice(6)) || 1;
  return `${y}-${String((n - 1) * 3 + 1).padStart(2, "0")}` as YearMonth;
}

export function distinctQuarters(months: YearMonth[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const ym of months) {
    const q = quarterOf(ym);
    if (!seen.has(q)) {
      seen.add(q);
      out.push(q);
    }
  }
  return out;
}

export function distinctYears(months: YearMonth[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const ym of months) {
    const y = ym.slice(0, 4);
    if (!seen.has(y)) {
      seen.add(y);
      out.push(y);
    }
  }
  return out;
}

export function aggregateMonthlyToQuarterly(months: YearMonth[], monthly: number[]): { quarters: string[]; values: number[] } {
  const quarters = distinctQuarters(months);
  const qMap = new Map<string, number>();
  quarters.forEach((q) => qMap.set(q, 0));
  months.forEach((ym, i) => {
    const q = quarterOf(ym);
    qMap.set(q, (qMap.get(q) ?? 0) + (monthly[i] ?? 0));
  });
  return {
    quarters,
    values: quarters.map((q) => Math.round((qMap.get(q) ?? 0) * 10) / 10),
  };
}

/* ------------------------------------------------------------------------------------------------
 * Assignment math
 * ---------------------------------------------------------------------------------------------- */

/** Monthly unit rate applied to an assignment (override → resource default → fallback). */
export function rateOf(asg: ProjectResourceAssignment, resource: UserResource | undefined, fallback = 10_000_000): number {
  return asg.rateOverride?.monthlyRate ?? resource?.defaultMonthlyRate ?? fallback;
}

export function isAssignmentActive(
  asg: Pick<ProjectResourceAssignment, "startDate" | "endDate">,
  ym: YearMonth,
): boolean {
  return ym >= monthOf(asg.startDate) && ym <= monthOf(asg.endDate);
}

/** M/M contributed by an assignment in a given month (override wins over flat ratio). */
export function assignmentMonthMM(
  asg: Pick<ProjectResourceAssignment, "startDate" | "endDate" | "allocationRatio" | "monthlyOverrides">,
  ym: YearMonth,
): number {
  if (!isAssignmentActive(asg, ym)) return 0;
  const o = asg.monthlyOverrides?.[ym];
  return typeof o === "number" ? o : asg.allocationRatio;
}

export function hasMonthOverride(asg: Pick<ProjectResourceAssignment, "monthlyOverrides">, ym: YearMonth): boolean {
  return typeof asg.monthlyOverrides?.[ym] === "number";
}

function roundMM(n: number): number {
  return Math.round(Math.max(0, n) * 10) / 10;
}

/** First and last ISO day of `YYYY-MM`. */
export function monthBounds(ym: YearMonth): { start: string; end: string } {
  const y = Number(ym.slice(0, 4));
  const m = Number(ym.slice(5, 7));
  const last = new Date(y, m, 0).getDate();
  return { start: `${ym}-01`, end: `${ym}-${String(last).padStart(2, "0")}` };
}

/** Drop overrides that fall outside the assignment window. */
export function clipMonthlyOverrides(
  overrides: Record<string, number> | undefined,
  startISO: string,
  endISO: string,
): Record<string, number> | undefined {
  if (!overrides) return undefined;
  const window = new Set(monthsBetween(startISO, endISO));
  const next: Record<string, number> = {};
  for (const [ym, v] of Object.entries(overrides)) {
    if (window.has(ym) && typeof v === "number") next[ym] = v;
  }
  return Object.keys(next).length ? next : undefined;
}

export function listMonthOverrides(
  overrides: Record<string, number> | undefined,
  startISO: string,
  endISO: string,
): Array<{ ym: YearMonth; value: number }> {
  const clipped = clipMonthlyOverrides(overrides, startISO, endISO);
  if (!clipped) return [];
  return Object.keys(clipped)
    .sort()
    .map((ym) => ({ ym, value: clipped[ym]! }));
}

/** Sum of month cells in the assignment window (override ?? baseline ratio). */
export function sumAssignmentMM(
  asg: Pick<ProjectResourceAssignment, "startDate" | "endDate" | "allocationRatio" | "monthlyOverrides">,
): number {
  const months = monthsBetween(asg.startDate, asg.endDate);
  const sum = months.reduce((s, ym) => s + assignmentMonthMM(asg, ym), 0);
  return roundMM(sum);
}

export function shiftMonth(ym: YearMonth, delta: number): YearMonth {
  let y = Number(ym.slice(0, 4));
  let m = Number(ym.slice(5, 7)) + delta;
  while (m > 12) {
    m -= 12;
    y += 1;
  }
  while (m < 1) {
    m += 12;
    y -= 1;
  }
  return `${y}-${String(m).padStart(2, "0")}`;
}

export function assignmentEdge(
  asg: Pick<ProjectResourceAssignment, "startDate" | "endDate">,
  ym: YearMonth,
): "start" | "end" | "both" | null {
  const startM = monthOf(asg.startDate);
  const endM = monthOf(asg.endDate);
  if (ym !== startM && ym !== endM) return null;
  if (startM === endM) return "both";
  return ym === startM ? "start" : "end";
}

/** Months strictly between the current window and `ym` (the gap that baseline will fill). */
export function gapMonthsTo(
  asg: Pick<ProjectResourceAssignment, "startDate" | "endDate">,
  ym: YearMonth,
): number {
  const startM = monthOf(asg.startDate);
  const endM = monthOf(asg.endDate);
  if (ym < startM) return Math.max(0, monthsBetween(`${ym}-01`, asg.startDate).length - 2);
  if (ym > endM) return Math.max(0, monthsBetween(asg.endDate, `${ym}-01`).length - 2);
  return 0;
}

function extendWindowTo(
  asg: Pick<ProjectResourceAssignment, "startDate" | "endDate">,
  ym: YearMonth,
): { startDate: string; endDate: string } {
  const startM = monthOf(asg.startDate);
  const endM = monthOf(asg.endDate);
  if (ym < startM) return { startDate: monthBounds(ym).start, endDate: asg.endDate };
  if (ym > endM) return { startDate: asg.startDate, endDate: monthBounds(ym).end };
  return { startDate: asg.startDate, endDate: asg.endDate };
}

function windowPatch(
  asg: Pick<ProjectResourceAssignment, "startDate" | "endDate" | "allocationRatio" | "monthlyOverrides">,
  startDate: string,
  endDate: string,
  overrides: Record<string, number> | undefined,
): { startDate: string; endDate: string; monthlyOverrides?: Record<string, number>; plannedMM: number } {
  const monthlyOverrides = clipMonthlyOverrides(overrides, startDate, endDate);
  return {
    startDate,
    endDate,
    monthlyOverrides,
    plannedMM: sumAssignmentMM({ ...asg, startDate, endDate, monthlyOverrides }),
  };
}

/**
 * Set or clear one month. `null` removes the exception so the baseline ratio applies again.
 * `plannedMM` is always the sum of visible cells.
 */
export function applyMonthOverride(
  asg: Pick<ProjectResourceAssignment, "startDate" | "endDate" | "allocationRatio" | "monthlyOverrides">,
  ym: YearMonth,
  value: number | null,
): { monthlyOverrides?: Record<string, number>; plannedMM: number } {
  const next = applyMonthCell(asg, ym, value);
  return { monthlyOverrides: next.monthlyOverrides, plannedMM: next.plannedMM };
}

/**
 * Edit one month cell. Outside the window this extends start/end (gap months get baseline).
 * A value equal to the baseline is stored as no exception.
 */
export function applyMonthCell(
  asg: Pick<ProjectResourceAssignment, "startDate" | "endDate" | "allocationRatio" | "monthlyOverrides">,
  ym: YearMonth,
  value: number | null,
): { startDate: string; endDate: string; monthlyOverrides?: Record<string, number>; plannedMM: number } {
  const window = value === null ? { startDate: asg.startDate, endDate: asg.endDate } : extendWindowTo(asg, ym);
  const next = { ...(asg.monthlyOverrides ?? {}) };
  if (value === null) delete next[ym];
  else {
    const rounded = roundMM(Math.min(1, Math.max(0, value)));
    if (rounded === roundMM(asg.allocationRatio)) delete next[ym];
    else next[ym] = rounded;
  }
  return windowPatch(asg, window.startDate, window.endDate, next);
}

/** Drop an edge month from the contract window. Interior months must use a 0 override instead. */
export function shrinkAssignmentEdge(
  asg: Pick<ProjectResourceAssignment, "startDate" | "endDate" | "allocationRatio" | "monthlyOverrides">,
  ym: YearMonth,
): { startDate: string; endDate: string; monthlyOverrides?: Record<string, number>; plannedMM: number } | null {
  const edge = assignmentEdge(asg, ym);
  if (!edge || edge === "both") return null;
  const startDate = edge === "start" ? monthBounds(shiftMonth(ym, 1)).start : asg.startDate;
  const endDate = edge === "end" ? monthBounds(shiftMonth(ym, -1)).end : asg.endDate;
  if (startDate > endDate) return null;
  const next = { ...(asg.monthlyOverrides ?? {}) };
  delete next[ym];
  return windowPatch(asg, startDate, endDate, next);
}

export function assignmentLaborCost(asg: ProjectResourceAssignment, resource: UserResource | undefined): number {
  return (asg.plannedMM || 0) * rateOf(asg, resource);
}

/** Approximate planned M/M from the assignment window and allocation ratio. */
export function estimatePlannedMM(startISO: string, endISO: string, ratio: number): number {
  const months = monthsBetween(startISO, endISO).length;
  if (!months) return 0;
  const d1 = new Date(startISO).getTime();
  const d2 = new Date(endISO).getTime();
  const days = Math.max(1, Math.round((d2 - d1) / 86_400_000) + 1);
  const approx = Math.max(0.1, days / 30.4);
  return Math.round(approx * ratio * 10) / 10;
}

/* ------------------------------------------------------------------------------------------------
 * Project-level cost summary (SI 대가 산정: 직접인건비 + 제경비 + 기술료 + 직접경비)
 * ---------------------------------------------------------------------------------------------- */

export interface ProjectCostSummary {
  projectId: string;
  projectName: string;
  headcount: number;
  plannedMM: number;
  directLabor: number;
  overhead: number;
  technicalFee: number;
  solution: number;
  expense: number;
  total: number;
  budget: number;
  remaining: number;
  /** 0..n (1 = 100%) */
  burn: number;
  solutionCount: number;
}

export function projectCostSummary(project: Project, resources: Record<string, UserResource>): ProjectCostSummary {
  const asgs = project.resourceAssignments ?? [];
  const sols = project.solutions ?? [];
  const budget = project.budget;
  const opts = budget?.costOptions;
  const directLabor = asgs.reduce((a, x) => a + assignmentLaborCost(x, resources[x.resourceId]), 0);
  const overhead = opts?.includeOverhead ? Math.round(directLabor * ((opts.overheadRatio ?? 1) - 1)) : 0;
  const technicalFee = opts?.includeTechnicalFee ? Math.round((directLabor + overhead) * (opts.technicalFeeRatio ?? 0)) : 0;
  const solution = sols.reduce((a, s) => a + (s.totalCost || 0), 0);
  const expense = budget?.targetExpenseCost ?? 0;
  const total = directLabor + overhead + technicalFee + solution + expense;
  const totalBudget = budget?.totalBudget ?? 0;
  return {
    projectId: project.id,
    projectName: project.name,
    headcount: asgs.length,
    plannedMM: Math.round(asgs.reduce((a, x) => a + (x.plannedMM || 0), 0) * 10) / 10,
    directLabor,
    overhead,
    technicalFee,
    solution,
    expense,
    total,
    budget: totalBudget,
    remaining: totalBudget - total,
    burn: totalBudget > 0 ? total / totalBudget : 0,
    solutionCount: sols.length,
  };
}

/* ------------------------------------------------------------------------------------------------
 * Monthly spread (for quarterly / timeline views)
 * ---------------------------------------------------------------------------------------------- */

/** Solution cost spread over its active months (once → first month, subscriptions → evenly). */
export function solutionMonthlyCost(sol: SolutionItem, ym: YearMonth, fallbackStart: string): number {
  const start = monthOf(sol.startDate ?? fallbackStart);
  const end = monthOf(sol.endDate ?? sol.startDate ?? fallbackStart);
  if (sol.billingCycle === "once") return ym === start ? sol.totalCost : 0;
  const months = monthsBetween(start + "-01", end + "-01");
  if (!months.length || ym < start || ym > end) return 0;
  return sol.totalCost / months.length;
}

export interface MonthBucket {
  ym: YearMonth;
  mm: number;
  labor: number;
  overhead: number;
  technicalFee: number;
  indirect: number;
  solution: number;
  expense: number;
  total: number;
}

/**
 * 비용이 놓이는 달. 플랜 시작·종료, 투입 기간, 솔루션 기간의 합집합.
 * 예산 화면의 월별 표와 현황의 월별 계획 비용이 같은 창을 쓴다.
 */
export function projectCostMonths(project: Project, projectFinish: string): YearMonth[] {
  const starts = [project.start, ...(project.resourceAssignments ?? []).map((a) => a.startDate), ...(project.solutions ?? []).map((s) => s.startDate ?? project.start)].filter(Boolean);
  const ends = [projectFinish, ...(project.resourceAssignments ?? []).map((a) => a.endDate), ...(project.solutions ?? []).map((s) => s.endDate ?? s.startDate ?? project.start)].filter(Boolean);
  if (!starts.length || !ends.length) return [];
  const lo = starts.reduce((m, d) => (d < m ? d : m));
  const hi = ends.reduce((m, d) => (d > m ? d : m));
  return monthsBetween(lo, hi);
}

export function projectMonthlyBuckets(project: Project, resources: Record<string, UserResource>, months: YearMonth[]): MonthBucket[] {
  const opts = project.budget?.costOptions;
  const totalExpense = project.budget?.targetExpenseCost ?? 0;
  const monthlyExpense = months.length > 0 ? totalExpense / months.length : 0;
  return months.map((ym) => {
    let mm = 0;
    let labor = 0;
    for (const asg of project.resourceAssignments ?? []) {
      const v = assignmentMonthMM(asg, ym);
      mm += v;
      labor += v * rateOf(asg, resources[asg.resourceId]);
    }
    const overhead = opts?.includeOverhead ? labor * ((opts.overheadRatio ?? 1) - 1) : 0;
    const technicalFee = opts?.includeTechnicalFee ? (labor + overhead) * (opts.technicalFeeRatio ?? 0) : 0;
    const indirect = overhead + technicalFee;
    let solution = 0;
    for (const sol of project.solutions ?? []) solution += solutionMonthlyCost(sol, ym, project.start);
    const expense = monthlyExpense;
    const total = labor + indirect + solution + expense;
    return { ym, mm, labor, overhead, technicalFee, indirect, solution, expense, total };
  });
}

/** Compact million-won formatter for matrix cells (e.g. 32M, 4.5M, 0.1M). */
export function fmtMillion(amount: number, masked = false): string {
  if (amount <= 0) return "·";
  if (masked) return "•••";
  const m = amount / 1_000_000;
  if (m < 0.1) return "0.1M";
  const rounded1 = Math.round(m * 10) / 10;
  if (rounded1 >= 100 || Number.isInteger(rounded1)) return `${Math.round(rounded1)}M`;
  return `${rounded1.toFixed(1)}M`;
}

/** Union of months covered by any assignment or solution in the given projects (falls back to project span). */
export function portfolioMonths(projects: Project[], opts: { includeSolutions?: boolean } = {}): YearMonth[] {
  let lo = "";
  let hi = "";
  const take = (a?: string, b?: string) => {
    if (a && (!lo || a < lo)) lo = a;
    if (b && (!hi || b > hi)) hi = b;
  };
  for (const p of projects) {
    take(p.start, p.start);
    for (const a of p.resourceAssignments ?? []) take(a.startDate, a.endDate);
    if (opts.includeSolutions) for (const s of p.solutions ?? []) take(s.startDate, s.endDate ?? s.startDate);
  }
  if (!lo || !hi) return [];
  return monthsBetween(lo, hi);
}

/* ------------------------------------------------------------------------------------------------
 * Grouping helpers for portfolio views
 * ---------------------------------------------------------------------------------------------- */

export interface GroupRow {
  key: string;
  label: string;
  hint?: string;
  color?: string;
  icon?: string;
  headcount: number;
  plannedMM: number;
  labor: number;
  solution: number;
  total: number;
  budget?: number;
}

/** 한 사람의 플랜별 계획. 같은 플랜의 배정은 한 줄로 합친다. */
export interface PersonPlanSlice {
  projectId: string;
  projectName: string;
  plannedMM: number;
  labor: number;
  monthly: number[];
}

/**
 * 사람 → 플랜 줄. 월 칸은 `months`와 같은 순서다.
 * 플랜이 하나면 길이 1이고, 화면은 그 줄을 펼치지 않는다.
 */
export function personPlanSlices(
  projects: readonly Project[],
  resources: Record<string, UserResource>,
  months: readonly YearMonth[],
): Map<string, PersonPlanSlice[]> {
  const byPerson = new Map<string, Map<string, PersonPlanSlice>>();
  for (const project of projects) {
    for (const assignment of project.resourceAssignments ?? []) {
      let plans = byPerson.get(assignment.resourceId);
      if (!plans) {
        plans = new Map();
        byPerson.set(assignment.resourceId, plans);
      }
      let slice = plans.get(project.id);
      if (!slice) {
        slice = {
          projectId: project.id,
          projectName: project.name,
          plannedMM: 0,
          labor: 0,
          monthly: months.map(() => 0),
        };
        plans.set(project.id, slice);
      }
      slice.plannedMM += assignment.plannedMM || 0;
      slice.labor += assignmentLaborCost(assignment, resources[assignment.resourceId]);
      months.forEach((ym, index) => {
        slice.monthly[index] = (slice.monthly[index] ?? 0) + assignmentMonthMM(assignment, ym);
      });
    }
  }
  const out = new Map<string, PersonPlanSlice[]>();
  for (const [personId, plans] of byPerson) {
    const rows = [...plans.values()].map((slice) => ({
      ...slice,
      plannedMM: Math.round(slice.plannedMM * 10) / 10,
    }));
    rows.sort((a, b) => b.plannedMM - a.plannedMM || a.projectName.localeCompare(b.projectName, "ko"));
    out.set(personId, rows);
  }
  return out;
}

/** 여러 플랜 합산 투입률이 이 값을 넘으면 과부하. */
export const ALLOCATION_OVERLOAD = 1.05;

export function resourceAllocation(projects: Project[]): Map<string, number> {
  const load = new Map<string, number>();
  for (const project of projects) {
    for (const assignment of project.resourceAssignments ?? []) {
      load.set(assignment.resourceId, (load.get(assignment.resourceId) ?? 0) + assignment.allocationRatio);
    }
  }
  return load;
}

export function overloadedResourceIds(projects: Project[]): Set<string> {
  const ids = new Set<string>();
  for (const [id, ratio] of resourceAllocation(projects)) {
    if (ratio > ALLOCATION_OVERLOAD) ids.add(id);
  }
  return ids;
}

/** 투입이 넘는 사람이 있거나, 승인 예산보다 예상 소진이 큰 플랜. */
export function attentionProjectIds(projects: Project[], resources: Record<string, UserResource>): Set<string> {
  const overloaded = overloadedResourceIds(projects);
  const ids = new Set<string>();
  for (const project of projects) {
    const assigned = (project.resourceAssignments ?? []).some((assignment) => overloaded.has(assignment.resourceId));
    const summary = projectCostSummary(project, resources);
    const overBudget = summary.budget > 0 && summary.total > summary.budget;
    if (assigned || overBudget) ids.add(project.id);
  }
  return ids;
}

export function sumRows(rows: GroupRow[]): GroupRow {
  return rows.reduce<GroupRow>(
    (acc, r) => ({
      ...acc,
      headcount: acc.headcount + r.headcount,
      plannedMM: Math.round((acc.plannedMM + r.plannedMM) * 10) / 10,
      labor: acc.labor + r.labor,
      solution: acc.solution + r.solution,
      total: acc.total + r.total,
      budget: (acc.budget ?? 0) + (r.budget ?? 0),
    }),
    { key: "__total", label: "합계", headcount: 0, plannedMM: 0, labor: 0, solution: 0, total: 0, budget: 0 },
  );
}
